import StripeExpressPay from './stripe/express.js';
import RyftExpressPay from './ryft/express.js';


class ExpressPay {

    /*
      Initializes ExpressPay from a given container element
    */

    constructor(container) {
        if (!container) {
            throw new Error("Container element is required to initialize ExpressPay.");
        }
        this.init(container);
    }

    async init(container) {

      const paymentProcessor = container.getAttribute("data-payment-processor");
      const country = container.getAttribute("data-country");
      const currency = container.getAttribute("data-currency");
      const publicKey = container.getAttribute("data-public-key");
      const affiliateId = parseInt(container.querySelector(".shipping-option")?.getAttribute("data-affiliate-id"), 10);
      const productVariantId = parseInt(container.getAttribute("data-product-variant-id"), 10);
      const productName = container.getAttribute("data-product-name");
      const productVariantName = container.getAttribute("data-product-variant-name");
      const productVariantPrice = parseInt(container.getAttribute("data-product-variant-price"), 10);
      const productVariantInStock = container.getAttribute("data-in-stock") === "True";
      const numberDeliveryOptions = parseInt(container.getAttribute("data-number-delivery-options"), 10);
      const paymentDisplayName = productVariantName ? `${productName} - ${productVariantName}` : productName;

      if (!productVariantInStock) {
        console.warn("Product variant is out of stock. Express Pay buttons will not be initialized.");
        return;
      }

      if (numberDeliveryOptions === 0) {
        console.warn("No delivery options available. Express Pay buttons will not be initialized.");
        return;
      }

      const shippingOptions = this.getShippingOptions(container);
      const defaultShipping = shippingOptions.length > 0 ? shippingOptions[0].amount : 0;
      const initialTotal = productVariantPrice + defaultShipping;

      const paymentRequestOptions = {
        country: country,
        currency: currency,
        total: { label: paymentDisplayName, amount: initialTotal },
        requestPayerName: true,
        requestPayerEmail: true,
        requestShipping: true,
        shippingOptions: shippingOptions.map(option => ({
          id: option.id,
          label: option.label,
          detail: option.detail,
          amount: option.amount,
        })),
      };

      if (paymentProcessor === "stripe") {
        await this.initStripeExpressPay(
            container,
            publicKey,
            affiliateId,
            productVariantId,
            shippingOptions,
            paymentDisplayName,
            productVariantPrice,
            paymentRequestOptions
        );
      } else if (paymentProcessor === "ryft") {
        await this.initRyftExpressPay(
            container,
            publicKey,
            affiliateId,
            productVariantId,
            shippingOptions,
            paymentDisplayName,
            productVariantPrice,
            paymentRequestOptions
        );
      } else {
        console.warn(`Unsupported payment processor: ${paymentProcessor}. Express Pay buttons will not be initialized.`);
      }
  }

  getShippingOptions(container) {
      const shippingOptions = [];
      container.querySelectorAll(".shipping-option").forEach((option) => {
        shippingOptions.push({
          id: option.getAttribute("data-service-id"),
          label: option.getAttribute("data-label"),
          leadTime: option.getAttribute("data-lead-time"),
          leadTimeMin: option.getAttribute("data-lead-time-min"),
          detail: option.getAttribute("data-detail"),
          amount: parseInt(option.getAttribute("data-gross-price"), 10),
          countries: Array.from(option.querySelectorAll(".shipping-country")).map(countryDiv => ({
            id: countryDiv.getAttribute("data-country-id"),
            code: countryDiv.getAttribute("data-country-code"),
            name: countryDiv.getAttribute("data-country-name"),
          })),
        });
      });
      return shippingOptions;
  }

  async initStripeExpressPay(
    container,
    publicKey,
    affiliateId,
    productVariantId,
    shippingOptions,
    paymentDisplayName,
    productVariantPrice,
    paymentRequestOptions
  ) {
    const stripeExpressPay = new StripeExpressPay(
      publicKey,
      affiliateId,
      productVariantId,
      shippingOptions,
      paymentDisplayName,
      productVariantPrice,
      paymentRequestOptions
    );

    await stripeExpressPay.init();
    const expressPayMounted = await stripeExpressPay.createExpressPayButton(container);
    if (!expressPayMounted) {
      const expressPayContainer = document.getElementById('expressPay');
      if (expressPayContainer) {
        expressPayContainer.style.display = 'none';
      }
      return;
    }
  }

  async initRyftExpressPay(
    container, 
    publicKey,
    affiliateId,
    productVariantId,
    shippingOptions,
    paymentDisplayName,
    productVariantPrice,
    paymentRequestOptions
  ) {
    const ryftExpressPay = new RyftExpressPay(
      publicKey,
      affiliateId,
      productVariantId,
      shippingOptions,
      paymentDisplayName,
      productVariantPrice,
      paymentRequestOptions
    );

    await ryftExpressPay.init();
    const expressPayMounted = await ryftExpressPay.createExpressPayButton(container);
    if (!expressPayMounted) {
      const expressPayContainer = document.getElementById('expressPay');
      if (expressPayContainer) {
        expressPayContainer.style.display = 'none';
      }
      return;
    }
  }
}

export default ExpressPay;